import * as React from "react";
import { FileDown } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { ApiError, downloadRequestReport } from "../../api";

function saveBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Downloads one PDF for a travel request: its full summary followed by
 *  every attached bill. "icon" sits beside a table row's View button;
 *  "full" is the labelled button in the request detail sheet. */
export function DownloadReportButton({
  travelRequestId,
  variant = "icon"
}: {
  travelRequestId: string;
  variant?: "icon" | "full";
}) {
  const [busy, setBusy] = React.useState(false);

  const download = async () => {
    setBusy(true);
    try {
      const { blob, filename } = await downloadRequestReport(travelRequestId);
      saveBlob(blob, filename);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        toast.error(
          "This request couldn't be found, or you no longer have access to it."
        );
      } else if (err instanceof ApiError && err.status === 409) {
        toast.error(err.message);
      } else {
        toast.error(
          "Couldn't download the request report. Check your connection and try again."
        );
      }
    } finally {
      setBusy(false);
    }
  };

  const icon = busy ? (
    <Spinner className="h-3.5 w-3.5" />
  ) : (
    <FileDown className="h-3.5 w-3.5" />
  );

  if (variant === "full") {
    return (
      <Button
        type="button"
        variant="outline"
        // h-8, matching SplitActionButton's own explicit height - the two
        // sit side by side in RequestDetailDialog's footer since the
        // Approve/Reject actions were added there, and Button's own
        // default size (h-10) left them visibly mismatched. min-w-0 lets
        // this button shrink below its label's natural width on a narrow
        // phone (the SplitActionButton beside it keeps its own full
        // width - this is the one that gives way) and the label itself
        // truncates with an ellipsis rather than crowding/overlapping it.
        className="h-8 min-w-0"
        onClick={download}
        disabled={busy}
      >
        <span className="mr-1.5 shrink-0">{icon}</span>
        <span className="truncate">
          {busy ? "Preparing report…" : "Download report"}
        </span>
      </Button>
    );
  }

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={download}
            disabled={busy}
            aria-label="Download report (PDF)"
          >
            {icon}
          </Button>
        </TooltipTrigger>
        <TooltipContent>Download report (PDF)</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
